import { type JSX } from "react";
import type { components } from "../../../api/schema";

type Scene = components["schemas"]["Scene"];
type WorldlineNode = components["schemas"]["WorldlineNode"];
type Character = components["schemas"]["Character"];
type WorldlineCharacterState = components["schemas"]["WorldlineCharacterState"];
type Choice = components["schemas"]["Choice"];

export type SceneDetailPanelProps = {
  scene: Scene;
  worldline: WorldlineNode;
  /** Scenario.characters (Type A/B identity)。欠損時はキャラ identity セクションを情報なしに。 */
  characters?: Character[];
  /** Worldline 固有のキャラ状態 (Type C/D state/fate)。欠損時は state セクションを情報なしに。 */
  characterStates?: WorldlineCharacterState[];
  /** その scene で プレイヤーが既に選んだ choice id (choiceList の分類用)。null は advance 進行。 */
  takenChoiceId?: string | null;
  /**
   * 既に別筆跡で分岐済の `choiceId → worldlineId` マップ。未指定は「既存分岐なし」扱い。
   * ある choiceId がこのマップに含まれる場合 → "既存筆跡に切替" ボタンを併記する。
   */
  existingBranches?: Record<string, string>;
  /** 「ここから別選択で分岐」ボタン押下時のコールバック。 */
  onForkFromChoice: (
    worldlineId: string,
    sceneId: string,
    untakenChoiceId: string,
  ) => void;
  /** 「既存筆跡に切替」ボタン押下時のコールバック。 */
  onSwitchWorldline?: (worldlineId: string) => void;
};

type ChoiceClassification =
  | { kind: "taken"; choice: Choice }
  | { kind: "existing"; choice: Choice; worldlineId: string }
  | { kind: "untaken"; choice: Choice };

function classifyChoice(
  choice: Choice,
  takenChoiceId: string | null | undefined,
  existingBranches: Record<string, string> | undefined,
): ChoiceClassification {
  if (takenChoiceId !== undefined && takenChoiceId !== null && choice.id === takenChoiceId) {
    return { kind: "taken", choice };
  }
  const existing = existingBranches?.[choice.id];
  if (existing !== undefined) {
    return { kind: "existing", choice, worldlineId: existing };
  }
  return { kind: "untaken", choice };
}

/**
 * Character identity (Type A/B) を 1 行で表示するヘルパ。
 * 情報欠損時 (name 欠落等) は "情報なし" プレースホルダで継続描画。
 */
function renderCharacterIdentity(char: Character): JSX.Element {
  const hasName = typeof char.name === "string" && char.name.length > 0;
  const hasRole = typeof char.role === "string" && char.role.length > 0;
  return (
    <li
      key={char.id}
      data-testid={`palimpsest-char-identity-${char.id}`}
      className="flex items-start gap-2 text-sm"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 w-3 h-3 rounded-full"
        style={{ backgroundColor: char.color ?? "#6366F1" }}
      />
      <div className="flex-1">
        <div className="text-slate-100">
          {hasName ? char.name : <span className="text-slate-500">情報なし</span>}
          {hasRole && (
            <span className="ml-2 text-xs text-slate-400">({char.role})</span>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Worldline 固有のキャラ状態 (Type C/D) を 1 行で表示するヘルパ。
 * 全 field が optional のため、少なくとも 1 つでも有値なら 1 行描画、
 * 全て空なら null (呼出側で「情報なし」フォールバック)。
 */
function renderCharacterState(state: WorldlineCharacterState): JSX.Element | null {
  const fragments: JSX.Element[] = [];
  if (typeof state.currentLocation === "string" && state.currentLocation.length > 0) {
    fragments.push(
      <span key="loc" className="text-xs">
        <span className="text-slate-400">場所:</span>{" "}
        <span className="text-slate-200">{state.currentLocation}</span>
      </span>,
    );
  }
  if (typeof state.currentEmotionalState === "string" && state.currentEmotionalState.length > 0) {
    fragments.push(
      <span key="emo" className="text-xs">
        <span className="text-slate-400">感情:</span>{" "}
        <span className="text-slate-200">{state.currentEmotionalState}</span>
      </span>,
    );
  }
  if (typeof state.currentWant === "string" && state.currentWant.length > 0) {
    fragments.push(
      <span key="want" className="text-xs">
        <span className="text-slate-400">欲求:</span>{" "}
        <span className="text-slate-200">{state.currentWant}</span>
      </span>,
    );
  }
  if (typeof state.fate === "string") {
    fragments.push(
      <span key="fate" className="text-xs">
        <span className="text-slate-400">運命:</span>{" "}
        <span className="text-amber-300">{state.fate}</span>
      </span>,
    );
  }
  if (fragments.length === 0) return null;
  return (
    <li
      key={state.characterId}
      data-testid={`palimpsest-char-state-${state.characterId}`}
      className="flex flex-col gap-0.5 py-1 border-t border-slate-800/60 first:border-t-0"
    >
      <span className="text-[10px] uppercase text-slate-500">
        {state.characterId}
      </span>
      <div className="flex flex-wrap gap-2">{fragments}</div>
    </li>
  );
}

/**
 * worldline-palimpsest (Phase 3-1, Task 5.4) scene 詳細パネル。
 *
 * palimpsest grid で scene ノード tap 時に開く drawer 相当。scene の segments / choices
 * に加え、キャラ identity (Type A/B) とキャラ状態差分 (Type C/D) を表示し、未踏 choice
 * から分岐 / 既存筆跡へ切替を起動する。
 *
 * 情報欠損時は「情報なし」プレースホルダで描画継続 (Req 12.3)。
 */
export function SceneDetailPanel({
  scene,
  worldline,
  characters,
  characterStates,
  takenChoiceId,
  existingBranches,
  onForkFromChoice,
  onSwitchWorldline,
}: SceneDetailPanelProps): JSX.Element {
  const hasCharacters = characters !== undefined && characters.length > 0;
  const stateRows = (characterStates ?? [])
    .map((s) => renderCharacterState(s))
    .filter((el): el is JSX.Element => el !== null);

  return (
    <section
      data-testid={`palimpsest-scene-detail-${scene.id}`}
      className="flex flex-col gap-3 p-3 bg-slate-900/80 border border-slate-700/60 rounded text-slate-200"
      aria-label="scene 詳細"
    >
      {/* セクション 1: scene 本文 (segments) */}
      <div data-testid="palimpsest-scene-detail-segments">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1">
          本文
        </h3>
        {scene.segments.length === 0 ? (
          <p className="text-sm text-slate-500">情報なし</p>
        ) : (
          <div className="text-sm leading-relaxed space-y-1">
            {scene.segments.map((seg, i) => {
              if (seg.type === "narration") {
                return (
                  <p key={i} className="text-slate-200">
                    {seg.text}
                  </p>
                );
              }
              return (
                <p key={i} className="text-slate-200">
                  <span className="text-slate-400">{seg.speakerName}：</span>
                  {seg.text}
                </p>
              );
            })}
          </div>
        )}
      </div>

      {/* セクション 2: Type A/B 登場人物 (identity) */}
      <div data-testid="palimpsest-scene-detail-characters">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1">
          登場人物
        </h3>
        {hasCharacters ? (
          <ul className="flex flex-col gap-1">
            {characters.map((c) => renderCharacterIdentity(c))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">情報なし</p>
        )}
      </div>

      {/* セクション 3: Type C/D 筆跡固有のキャラ状態 */}
      <div data-testid="palimpsest-scene-detail-character-states">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1">
          この筆跡でのキャラ状態
        </h3>
        {stateRows.length > 0 ? (
          <ul className="flex flex-col">{stateRows}</ul>
        ) : (
          <p className="text-sm text-slate-500">情報なし</p>
        )}
      </div>

      {/* セクション 4: choices + fork / 切替 ボタン */}
      <div data-testid="palimpsest-scene-detail-choices">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1">
          選択肢
        </h3>
        {scene.choices.length === 0 ? (
          <p className="text-sm text-slate-500">
            {scene.isEnding ? "エンディング到達 (選択肢なし)" : "情報なし"}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {scene.choices.map((choice) => {
              const cls = classifyChoice(choice, takenChoiceId, existingBranches);
              return (
                <li
                  key={choice.id}
                  data-testid={`palimpsest-scene-detail-choice-${choice.id}`}
                  data-choice-kind={cls.kind}
                  className="flex items-start gap-2 text-sm"
                >
                  <span className="flex-1 text-slate-200">{choice.text}</span>
                  {cls.kind === "taken" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                      選択済
                    </span>
                  )}
                  {cls.kind === "existing" && onSwitchWorldline !== undefined && (
                    <button
                      type="button"
                      data-testid={`palimpsest-switch-worldline-${choice.id}`}
                      onClick={() => onSwitchWorldline(cls.worldlineId)}
                      className="text-[10px] px-2 py-0.5 rounded border border-indigo-600/60 bg-indigo-900/40 text-indigo-200 hover:bg-indigo-800"
                    >
                      既存筆跡に切替
                    </button>
                  )}
                  {cls.kind === "untaken" && (
                    <button
                      type="button"
                      data-testid={`palimpsest-fork-from-choice-${choice.id}`}
                      onClick={() =>
                        onForkFromChoice(worldline.id, scene.id, choice.id)
                      }
                      className="text-[10px] px-2 py-0.5 rounded border border-amber-600/60 bg-amber-900/40 text-amber-200 hover:bg-amber-800"
                    >
                      ここから別選択で分岐
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
